import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { OrderDetailModal } from '../components/widgets/OrderDetailModal';
import { Orders as OrdersApi } from '../api/client';
import { DollarSign, User, Calendar, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { useStore } from '../store';

type OrderStats = {
  last_30d_revenue: number;
  last_30d_orders: number;
  avg_ticket: number;
  revenue_delta_pct: number | null;
  orders_delta_pct: number | null;
  top_sku: { name: string; units: number } | null;
  top_sku_share: number;
};

function fmtMoney(n: number) {
  return `$${n.toFixed(2)}`;
}
function fmtDelta(p: number | null) {
  if (p === null) return 'no baseline';
  const sign = p >= 0 ? '+' : '';
  return `${sign}${p.toFixed(1)}% vs prior 30d`;
}

export function Orders() {
  const [orders, setOrders] = useState<any[] | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const pushToast = useStore((s) => s.pushToast);

  const load = () => {
    OrdersApi.list().then(setOrders).catch(() => setOrders([]));
    fetch('/api/orders/stats').then((r) => r.json()).then((j) => setStats(j.data ?? null)).catch(() => setStats(null));
  };
  useEffect(load, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await fetch('/api/orders/sync', { method: 'POST' }).then((r) => r.json());
      if (r.ok) {
        pushToast({ kind: 'success', message: `Synced ${r.data?.synced ?? 0} orders from Etsy` });
        load();
      } else {
        pushToast({ kind: 'error', message: r.error?.message ?? 'sync failed' });
      }
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div>
      <PageHeader
        kicker="// orders · 06"
        title="Recent orders"
        description="Synced from Etsy. Newest first."
        actions={
          <Button variant="secondary" leftIcon={<RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />} onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from Etsy'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <Card className="p-5">
          <div className="kicker mb-1.5">last 30d</div>
          <div className="serif text-[28px] font-semibold text-ink-800 tabular-nums">
            {stats ? fmtMoney(stats.last_30d_revenue) : '—'}
          </div>
          <div className="kicker mt-1 text-ink-500">
            {stats ? `${stats.last_30d_orders} order${stats.last_30d_orders === 1 ? '' : 's'}` : '—'}
            {stats?.orders_delta_pct != null && <> · {fmtDelta(stats.orders_delta_pct)}</>}
          </div>
        </Card>
        <Card className="p-5">
          <div className="kicker mb-1.5">avg ticket</div>
          <div className="serif text-[28px] font-semibold text-ink-800 tabular-nums">
            {stats ? fmtMoney(stats.avg_ticket) : '—'}
          </div>
          <div className="kicker mt-1 text-ink-500">
            {stats?.revenue_delta_pct != null ? fmtDelta(stats.revenue_delta_pct) : 'no baseline'}
          </div>
        </Card>
        <Card className="p-5">
          <div className="kicker mb-1.5">top sku</div>
          <div className="text-[14px] text-ink-800 truncate font-medium">
            {stats?.top_sku ? stats.top_sku.name : '—'}
          </div>
          <div className="kicker mt-1 text-ink-500">
            {stats?.top_sku
              ? `${stats.top_sku.units} unit${stats.top_sku.units === 1 ? '' : 's'} · ${(stats.top_sku_share * 100).toFixed(0)}% of sales`
              : 'no data'}
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 pt-5">
          <CardHeader kicker="// all" title="Orders" />
        </div>
        <div className="grid grid-cols-[1fr_180px_120px_120px_120px] px-4 py-3 border-y border-cream-300 kicker bg-cream-50/40">
          <div>Order</div>
          <div>Buyer</div>
          <div>Total</div>
          <div>Status</div>
          <div className="text-right">Date</div>
        </div>

        {orders === null
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="px-4 py-3"><Skeleton className="h-10" /></div>
            ))
          : orders.length === 0
            ? <div className="px-4 py-16 text-center text-[12px] text-ink-500">No orders yet. Click Sync from Etsy to fetch paid receipts.</div>
            : orders.map((o, i) => (
                <motion.div
                  key={o.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => setSelectedOrderId(o.etsy_order_id)}
                  className="grid grid-cols-[1fr_180px_120px_120px_120px] px-4 py-3 border-b border-cream-200 hover:bg-cream-50/60 transition items-center cursor-pointer"
                >
                  <div className="min-w-0">
                    <div className="text-[12.5px] text-ink-800 font-mono font-medium">#{o.etsy_order_id}</div>
                    <div className="kicker mt-0.5 text-ink-400">ORD · {o.id}</div>
                  </div>
                  <div className="min-w-0 pr-2">
                    <div className="text-[12.5px] text-ink-700 truncate flex items-center gap-1.5">
                      <User size={11} className="text-ink-400" />
                      {o.buyer_email ?? '—'}
                    </div>
                    <div className="text-[11.5px] text-ink-500 truncate">{o.name}</div>
                  </div>
                  <div className="text-[12.5px] tabular-nums text-ink-800 flex items-center gap-1 font-medium">
                    <DollarSign size={11} className="text-ink-400" />
                    {o.total_amount}
                  </div>
                  <div>
                    <Badge tone={o.status === 'paid' ? 'success' : o.status === 'shipped' ? 'info' : 'neutral'}>{o.status}</Badge>
                  </div>
                  <div className="text-right text-[11.5px] text-ink-500 tabular-nums flex items-center justify-end gap-1">
                    <Calendar size={11} className="text-cream-300" />
                    {new Date(o.created_at * 1000).toLocaleDateString()}
                  </div>
                </motion.div>
              ))}
      </Card>

      <OrderDetailModal
        etsyOrderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
    </div>
  );
}
